import { useCallback, useEffect, useMemo, useState } from 'react'
import { gql, useLazyQuery, useMutation } from '@apollo/client'
import { useAuth } from '@hooks/useAuth'
import config from '@utils/config'

const TOS_QUERY = gql`
  query($service: Service!) {
    unagreedConsents(service: $service) {
      termCode
      required
      title
      subTitle
      contentUrl
      defaultChecked
    }
  }
`

const TOS_MUTATION = gql`
  mutation($service: Service!, $agreements: [Agreement]!) {
    result: sendAgreements(service: $service, agreements: $agreements)
  }
`

export interface Agreement {
  termCode: string
  agreed: boolean
}

export interface ConsentItem {
  termCode: string
  required: boolean
  title: string
  subTitle?: string
  contentUrl: string
  defaultChecked?: boolean
}

interface TosV2Context {
  consentItems: Array<ConsentItem> | undefined
  isReady: boolean
  isTosAgreed: boolean
  checkTos: () => void
  sendAgreements: (agreements: Agreement[]) => void
}

const useTosV2 = (service: string): TosV2Context => {
  const { publicRuntimeConfig: { ENABLE_TOS_V2_FEATURE: enableTosV2 } } = config

  const [consentItems, setConsentItems] = useState(undefined)

  const { isAuthenticated } = useAuth()

  const [getTosInfo, { loading: queryLoading }] = useLazyQuery(TOS_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: ({ unagreedConsents }) => setConsentItems(unagreedConsents),
    onError: () => setConsentItems([])
  })
  const checkTos = useCallback(() => {
    if (!isAuthenticated) return
    getTosInfo({ variables: { service } })
  }, [isAuthenticated, service, getTosInfo])

  const [sendTos, { loading: mutationLoading }] = useMutation(TOS_MUTATION, {
    onCompleted: ({ result }) => {
      if (result) {
        setConsentItems([])
      }
    }
  })
  const sendAgreements = useCallback((agreements: Agreement[]) => {
    if (!isAuthenticated) return
    sendTos({ variables: { service, agreements } })
  }, [isAuthenticated, service, sendTos])

  const isTosAgreed = useMemo(() => {
    return consentItems?.every(item => !item.required)
  }, [consentItems])

  useEffect(() => {
    if (enableTosV2) checkTos()
  }, [checkTos, enableTosV2])

  return enableTosV2
    ? {
        consentItems,
        isReady: !queryLoading && !mutationLoading,
        isTosAgreed,
        checkTos,
        sendAgreements
      }
    : {
        consentItems: undefined,
        isReady: true,
        isTosAgreed: true,
        checkTos: () => undefined,
        sendAgreements: () => undefined
      }
}

export default useTosV2
